// Back-office mock-up data: the ready-made designs ("modèles"), shaped like the model the developers
// built on main (category scope TEMPLATE, template, template_product_variant, isLocked per field
// inside the projectJson). The themes are the
// client's "Arborescence des modèles de personnalisation" v1 (catalogue-references.md §3); the
// published designs are the ones the configurator prototype shows, so both tools list the same
// library; the drafts exist only here, as the customer never sees a draft.
import { ADMIN_REFERENCES } from '@/mocks/adminCatalog';
import { localizedTags } from '@/mocks/adminLocales';
import { EPrintStyle } from '@/typescript/enums/EPrintStyle';
import { fitsTemplate, referenceVariants, toVariantKey } from '@/utils/adminTemplateVariants';
import type { IAdminLocalizedTags, IAdminTranslations } from '@/mocks/adminLocales';

type TAdminTemplateFieldKind = 'decor' | 'image' | 'text';

interface IAdminTemplateField {
    content: string;
    id: string;
    isLocked: boolean;
    kind: TAdminTemplateFieldKind;
    name: string;
}

interface IAdminTemplate {
    description: string;
    fields: IAdminTemplateField[];
    id: string;
    // An image GobeletCup imported for the gallery: it wins over the editor's capture.
    importedImageUrl?: string;
    isActive: boolean;
    name: { en: string; fr: string; pt: string };
    // The variant the editor opens on: the reference, style and position it was drawn on.
    startVariantKey?: string;
    subCategory: string;
    tags: IAdminLocalizedTags;
    themeId: string;
    // The thumbnail made from the editor at the last save; none on the simulated designs.
    thumbnailUrl?: string;
    updatedAt: string;
    // Where the design is offered: reference × print style × position (utils/adminTemplateVariants).
    variantKeys: string[];
    // The zone keys the design fills; a variant must offer every one of them.
    zoneKeys: string[];
}

interface IAdminTemplateTheme {
    id: string;
    label: string;
    subCategories: string[];
    // Keyed by the sub-theme's French label.
    subCategoryTranslations?: Record<string, IAdminTranslations>;
    translations?: IAdminTranslations;
}

const ADMIN_TEMPLATE_THEMES: IAdminTemplateTheme[] = [
    {
        id: 'anniversaire',
        label: 'Anniversaire',
        translations: { en: 'Birthday', pt: 'Aniversário' },
        subCategories: ['Adultes', 'Enfants', 'Adolescents', 'Vos photos d’anniversaire'],
        subCategoryTranslations: {
            Adultes: { en: 'Adults', pt: 'Adultos' },
            Enfants: { en: 'Children', pt: 'Crianças' },
            Adolescents: { en: 'Teenagers', pt: 'Adolescentes' },
            'Vos photos d’anniversaire': { en: 'Your birthday photos', pt: '' },
        },
    },
    {
        id: 'mariage',
        label: 'Autour du mariage',
        translations: { en: 'Weddings', pt: 'Casamento' },
        subCategories: [
            'EVJF & EVG',
            'Anniversaire de mariage',
            'Champêtre, nature',
            'Mariage à thèmes',
            'Personnage',
            'Illustration',
            'Vos photos de mariage',
        ],
    },
    {
        id: 'naissance',
        label: 'Naissance & baptême',
        translations: { en: 'Birth & christening', pt: 'Nascimento & batizado' },
        subCategories: ['Naissance', 'Baptême', 'Baby shower', 'Gender reveal', 'Communion', 'Bar mitsva', 'Vos photos de naissance'],
    },
    {
        id: 'famille',
        label: 'Fêtes en famille & entre amis',
        translations: { en: 'Family & friends', pt: 'Família & amigos' },
        subCategories: [
            'Barbecue, garden party',
            'Cousinage',
            'Crémaillères',
            'Fête des pères, des mères',
            'Apéro',
            'Humour',
            'Vos photos de famille',
        ],
    },
    {
        id: 'evenementiel',
        label: 'Fêtes, événementiel, soirée à thème',
        translations: { en: 'Parties & events', pt: 'Festas & eventos' },
        subCategories: [
            'Nouvel an',
            'Noël',
            'Pâques',
            'Fête nationale',
            'Halloween',
            'Saint-Patrick',
            'Saint-Valentin',
            'Carnaval',
            'Soirée à thème',
            'Fêtes religieuses',
            'Vos photos de soirée',
        ],
    },
    {
        id: 'entreprise',
        label: 'Entreprise & professionnels',
        translations: { en: 'Business & professionals', pt: 'Empresas & profissionais' },
        subCategories: [
            'Métiers',
            'Au bureau',
            'Soirée d’entreprise',
            'Séminaires',
            'Formation',
            'Départ en retraite',
            'Vos photos d’entreprise',
        ],
    },
    {
        id: 'associatif',
        label: 'Sports & vie associative',
        translations: { en: 'Sports & clubs', pt: 'Desporto & associações' },
        subCategories: [
            'Sport',
            'École',
            'Comité des fêtes',
            'Bureau des étudiants (BDE)',
            'Solidarité',
            'Musique',
            'Jeux de carte',
            'Chasse & pêche',
            'Vos photos en visuel',
        ],
    },
    {
        id: 'motifs',
        label: 'Motifs graphiques',
        translations: { en: 'Graphic patterns', pt: '' },
        subCategories: ['Sélection de motifs & formes décoratives de remplissage'],
    },
    {
        id: 'photos',
        label: 'Vos photos en visuel',
        translations: { en: 'Your photos', pt: 'As suas fotos' },
        subCategories: [
            'Anniversaire',
            'Autour du mariage',
            'Naissances & baptême',
            'Fête en famille & entre amis',
            'Fête, événementiel, soirée à thème',
            'Professionnels & organisation',
            'Sport & vie associative',
        ],
    },
];

const CUPS = ['ECO120', 'ECO180', 'ECO200', 'ECO300', 'ECO400', 'ECO450', 'ECO500'];
const COCKTAILS = ['ECOTUBO25', 'ECOGRANITI35', 'ECOGRANITI40'];
const GLASSES = ['ECOVIN', 'ECOVIN25', 'ECOCHAMP'];
const SCREEN_STYLES = [EPrintStyle.MONOCOLOR, EPrintStyle.BICOLOR, EPrintStyle.QUADRI_SMALL_HEIGHT];
const QUADRI_STYLES = [EPrintStyle.QUADRI_SMALL_HEIGHT, EPrintStyle.QUADRI_LARGE_HEIGHT];

interface ITemplateSeed {
    caption: string;
    id: string;
    isDraft?: boolean;
    label: string;
    subCategory: string;
    themeId: string;
}

const SEEDS: ITemplateSeed[] = [
    { id: 'anniv-confettis', label: 'Confettis', caption: 'Joyeux anniversaire', subCategory: 'Adultes', themeId: 'anniversaire' },
    { id: 'anniv-grand-chiffre', label: 'Grand chiffre', caption: '30 ans', subCategory: 'Adultes', themeId: 'anniversaire' },
    { id: 'anniv-guirlande', label: 'Guirlande', caption: 'Guirlande', subCategory: 'Enfants', themeId: 'anniversaire' },
    { id: 'anniv-neon', label: 'Néon', caption: 'Party time', subCategory: 'Adolescents', themeId: 'anniversaire' },
    { id: 'anniv-ballons', label: 'Ballons', caption: 'Bon anniversaire', subCategory: 'Enfants', themeId: 'anniversaire', isDraft: true },
    { id: 'mariage-alliances', label: 'Alliances', caption: 'Emma & Louis', subCategory: 'Mariage à thèmes', themeId: 'mariage' },
    { id: 'mariage-champetre', label: 'Champêtre', caption: 'Notre mariage', subCategory: 'Champêtre, nature', themeId: 'mariage' },
    { id: 'mariage-evjf', label: 'EVJF doré', caption: 'Team mariée', subCategory: 'EVJF & EVG', themeId: 'mariage' },
    { id: 'mariage-monogramme', label: 'Monogramme', caption: 'E & L', subCategory: 'Illustration', themeId: 'mariage' },
    { id: 'naissance-petits-pieds', label: 'Petits pieds', caption: 'Bienvenue', subCategory: 'Naissance', themeId: 'naissance' },
    { id: 'naissance-nuage', label: 'Nuage', caption: 'Baby shower', subCategory: 'Baby shower', themeId: 'naissance' },
    { id: 'naissance-colombe', label: 'Colombe', caption: 'Mon baptême', subCategory: 'Baptême', themeId: 'naissance' },
    {
        id: 'naissance-gender',
        label: 'Bleu ou rose',
        caption: 'Fille ou garçon ?',
        subCategory: 'Gender reveal',
        themeId: 'naissance',
    },
    { id: 'famille-barbecue', label: 'Garden party', caption: 'Barbecue', subCategory: 'Barbecue, garden party', themeId: 'famille' },
    {
        id: 'famille-cremaillere',
        label: 'Crémaillère',
        caption: 'Pendaison de crémaillère',
        subCategory: 'Crémaillères',
        themeId: 'famille',
    },
    { id: 'famille-humour', label: 'Blague de comptoir', caption: 'Encore un verre ?', subCategory: 'Humour', themeId: 'famille' },
    { id: 'famille-apero', label: 'Apéro time', caption: 'C’est l’apéro', subCategory: 'Apéro', themeId: 'famille' },
    { id: 'event-nouvel-an', label: 'Feu d’artifice', caption: 'Bonne année', subCategory: 'Nouvel an', themeId: 'evenementiel' },
    { id: 'event-noel', label: 'Sapin', caption: 'Joyeux Noël', subCategory: 'Noël', themeId: 'evenementiel' },
    { id: 'event-halloween', label: 'Nuit noire', caption: 'Halloween', subCategory: 'Halloween', themeId: 'evenementiel' },
    { id: 'event-valentin', label: 'Saint-Valentin', caption: 'Je t’aime', subCategory: 'Saint-Valentin', themeId: 'evenementiel' },
    { id: 'event-carnaval', label: 'Masques', caption: 'Carnaval', subCategory: 'Carnaval', themeId: 'evenementiel', isDraft: true },
    { id: 'entreprise-logo', label: 'Logo centré', caption: 'Votre logo', subCategory: 'Métiers', themeId: 'entreprise' },
    { id: 'entreprise-seminaire', label: 'Séminaire', caption: 'Séminaire 2027', subCategory: 'Séminaires', themeId: 'entreprise' },
    {
        id: 'entreprise-soiree',
        label: 'Soirée annuelle',
        caption: 'Soirée d’entreprise',
        subCategory: 'Soirée d’entreprise',
        themeId: 'entreprise',
    },
    {
        id: 'entreprise-retraite',
        label: 'Pot de départ',
        caption: 'Bonne retraite',
        subCategory: 'Départ en retraite',
        themeId: 'entreprise',
    },
    { id: 'asso-podium', label: 'Podium', caption: 'Allez les bleus', subCategory: 'Sport', themeId: 'associatif' },
    { id: 'asso-kermesse', label: 'Kermesse', caption: 'Kermesse', subCategory: 'École', themeId: 'associatif' },
    { id: 'asso-comite', label: 'Comité des fêtes', caption: 'Comité des fêtes', subCategory: 'Comité des fêtes', themeId: 'associatif' },
    {
        id: 'asso-bde',
        label: 'Bureau des étudiants',
        caption: 'BDE',
        subCategory: 'Bureau des étudiants (BDE)',
        themeId: 'associatif',
    },
    {
        id: 'motif-terrazzo',
        label: 'Terrazzo',
        caption: '',
        subCategory: 'Sélection de motifs & formes décoratives de remplissage',
        themeId: 'motifs',
    },
    {
        id: 'motif-rayures',
        label: 'Rayures',
        caption: '',
        subCategory: 'Sélection de motifs & formes décoratives de remplissage',
        themeId: 'motifs',
    },
    {
        id: 'motif-vagues',
        label: 'Vagues',
        caption: '',
        subCategory: 'Sélection de motifs & formes décoratives de remplissage',
        themeId: 'motifs',
    },
    {
        id: 'motif-feuillage',
        label: 'Feuillage',
        caption: '',
        subCategory: 'Sélection de motifs & formes décoratives de remplissage',
        themeId: 'motifs',
    },
    { id: 'photo-plein', label: 'Photo plein gobelet', caption: 'Votre photo', subCategory: 'Anniversaire', themeId: 'photos' },
    {
        id: 'photo-texte',
        label: 'Photo & texte',
        caption: 'Votre photo + votre texte',
        subCategory: 'Autour du mariage',
        themeId: 'photos',
    },
    { id: 'photo-polaroid', label: 'Polaroid', caption: 'Polaroid', subCategory: 'Fête en famille & entre amis', themeId: 'photos' },
    {
        id: 'photo-trois',
        label: 'Triptyque',
        caption: 'Trois photos',
        subCategory: 'Sport & vie associative',
        themeId: 'photos',
    },
];

function buildFields(seed: ITemplateSeed): IAdminTemplateField[] {
    const fields: IAdminTemplateField[] = [];
    if (seed.themeId === 'photos' || seed.id === 'entreprise-logo') {
        const name = seed.id === 'entreprise-logo' ? 'Logo' : 'Photo';
        fields.push({ id: `${seed.id}-image`, kind: 'image', name, content: 'Emplacement vide', isLocked: false });
    }
    if (seed.caption && seed.themeId !== 'photos') {
        fields.push({ id: `${seed.id}-title`, kind: 'text', name: 'Texte principal', content: seed.caption, isLocked: false });
        fields.push({ id: `${seed.id}-subtitle`, kind: 'text', name: 'Date ou lieu', content: '14 juin 2027', isLocked: false });
    }
    fields.push({ id: `${seed.id}-decor`, kind: 'decor', name: 'Motif décoratif', content: seed.label, isLocked: true });
    if (seed.themeId === 'entreprise') {
        fields.push({ id: `${seed.id}-mention`, kind: 'text', name: 'Mention', content: 'Consommer avec modération', isLocked: true });
    }
    return fields;
}

function buildStyles(seed: ITemplateSeed): EPrintStyle[] {
    if (seed.themeId === 'photos' || seed.themeId === 'motifs') {
        return [...QUADRI_STYLES];
    }
    if (seed.id === 'entreprise-logo') {
        return [...SCREEN_STYLES, EPrintStyle.QUADRI_LARGE_HEIGHT];
    }
    return [...SCREEN_STYLES];
}

function buildReferences(seed: ITemplateSeed): string[] {
    if (seed.themeId === 'mariage') {
        return [...CUPS, ...GLASSES];
    }
    if (seed.themeId === 'photos' || seed.themeId === 'motifs') {
        return ['ECO200', 'ECO300', 'ECO400', 'ECO450', 'ECO500'];
    }
    if (seed.themeId === 'evenementiel') {
        return [...CUPS, ...COCKTAILS];
    }
    return [...CUPS];
}

// The variants of these references in these styles that have every zone of the design.
function buildVariantKeys(codes: string[], styles: EPrintStyle[], zoneKeys: string[]): string[] {
    return ADMIN_REFERENCES.filter((reference) => codes.includes(reference.code)).flatMap((reference) =>
        referenceVariants(reference)
            .filter((variant) => styles.includes(variant.style))
            .filter((variant) => fitsTemplate(zoneKeys, reference, variant.style))
            .map(toVariantKey),
    );
}

const UPDATED_AT = ['2026-09-23', '2026-09-21', '2026-09-19', '2026-09-16', '2026-09-12'];

const ADMIN_TEMPLATES: IAdminTemplate[] = [
    ...SEEDS.map((seed, index) => ({
        id: seed.id,
        name: { fr: seed.label, en: '', pt: '' },
        description: '',
        themeId: seed.themeId,
        subCategory: seed.subCategory,
        tags: localizedTags(
            [
                seed.subCategory.toLowerCase(),
                ...seed.caption
                    .toLowerCase()
                    .split(' ')
                    .filter((word) => word.length > 3),
            ].slice(0, 4),
        ),
        variantKeys: buildVariantKeys(buildReferences(seed), buildStyles(seed), ['main']),
        isActive: !seed.isDraft,
        updatedAt: UPDATED_AT[index % UPDATED_AT.length],
        fields: buildFields(seed),
        zoneKeys: ['main'],
    })),
    // A design made for the pitchers: it fills their two faces, so no single-zone cup can take it.
    {
        id: 'mariage-pichet-deux-faces',
        name: { fr: 'Pichet des mariés', en: '', pt: '' },
        description: 'Un prénom sur chaque face.',
        themeId: 'mariage',
        subCategory: 'Mariage à thèmes',
        tags: localizedTags(['pichet', 'mariés', 'prénoms'], ['jug', 'wedding'], []),
        variantKeys: buildVariantKeys(['ECOJAR100', 'ECOJAR150'], [EPrintStyle.MONOCOLOR, EPrintStyle.BICOLOR], ['front', 'back']),
        isActive: true,
        updatedAt: '2026-09-22',
        fields: [
            { id: 'pichet-front', kind: 'text', name: 'Prénom, face avant', content: 'Emma', isLocked: false },
            { id: 'pichet-back', kind: 'text', name: 'Prénom, face arrière', content: 'Louis', isLocked: false },
            { id: 'pichet-decor', kind: 'decor', name: 'Couronne de feuillage', content: 'Feuillage', isLocked: true },
        ],
        zoneKeys: ['front', 'back'],
    },
];

export { ADMIN_TEMPLATE_THEMES, ADMIN_TEMPLATES };

export type { IAdminTemplate, IAdminTemplateField, IAdminTemplateTheme };
